import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { useSubscription } from '@apollo/client';
import Info from '../../../../../../components/channel-configuration/info';
import { InputSelect } from '../../../../../../components/input/select';
import { useBackdropContext } from '../../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../../context/installation';
import {
  BinarySensorTypeInternal,
  OnPartialConfigChangedSubscription,
  OnPartialConfigChangedSubscriptionVariables,
} from '../../../../../../data-access/gql-types/graphql';
import { ON_PARTIAL_CONFIG_CHANGED } from '../../../../../../data-access/subscriptions/lavva-devices';
import { useTimeout } from '../../../../../../hooks/timeout/use-timeout';
import { toastSuccess } from '../../../../../../utils/toast';
import { useBinarySensorConfigure } from '../../../../api/binary-sensor/binary-sensor.hooks';
import { useDevicesAndChannelsContext } from '../../../../context/devices-and-channels';
import { ChannelBinarySensorType, ChannelType } from '../../../../types';
import { BinarySensorTypeRequest } from '../../../../types/graphql-rest-converter';
import { getNumericalValue } from '../../../../utils/channels/helpers/state';

type BinarySensorTypeProps = {
  channel: ChannelType;
};

const BinarySensorType: React.FC<BinarySensorTypeProps> = ({ channel }) => {
  const { t } = useTranslation('configuration');
  const { mutate } = useBinarySensorConfigure();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { setTimeoutError, clearTimeoutError } = useTimeout();
  const { updateChannelDetails, setChannelList } = useDevicesAndChannelsContext();
  const { skipLavvaFetch } = useInstallationContext();
  const { data: configurationData } = useSubscription<
    OnPartialConfigChangedSubscription,
    OnPartialConfigChangedSubscriptionVariables
  >(ON_PARTIAL_CONFIG_CHANGED, {
    variables: { channelId: channel.id },
    skip: skipLavvaFetch,
  });

  const updateLavvaBinarySensorType = (sensorType: BinarySensorTypeInternal) => {
    setChannelList((prev) => {
      const tempList = cloneDeep(prev);
      const index = tempList.findIndex((x) => x.id === channel.id);

      if (index !== -1) {
        (tempList[index].data as ChannelBinarySensorType).sensorType = sensorType;
      }

      return tempList;
    });

    updateChannelDetails((prev) => {
      if (prev?.id !== channel.id) return prev;

      const temp = cloneDeep(prev);
      if (temp) {
        (temp.data as ChannelBinarySensorType).sensorType = sensorType;
      }

      return temp;
    });
  };

  useEffect(() => {
    if (configurationData?.onPartialConfigChanged) {
      clearTimeoutError();
      const { numericalProperties } = configurationData.onPartialConfigChanged;

      const sensorType = getNumericalValue(numericalProperties, 'SensorType');

      if (sensorType?.value) {
        updateLavvaBinarySensorType(BinarySensorTypeInternal[sensorType.value]);
        toastSuccess({ content: t('sensorType.sensorSaved') });
      }

      turnOffBackdrop();
    }
  }, [configurationData]);

  const onChangeBinarySensorType = (value: BinarySensorTypeInternal) => {
    turnOnBackdrop();

    mutate(
      { channelId: channel.id, deviceId: channel.deviceId, value: BinarySensorTypeRequest[value] },
      {
        onSuccess: () => {
          setTimeoutError();
        },
      },
    );
  };

  const value = (channel.data as ChannelBinarySensorType).sensorType;

  return (
    <>
      <hr />
      <Info title={t('sensorType.label')} />
      <InputSelect
        value={value}
        onChange={onChangeBinarySensorType}
        options={[
          { label: t('sensorType.options.input'), value: BinarySensorTypeInternal.Input },
          { label: t('sensorType.options.movement'), value: BinarySensorTypeInternal.Movement },
          { label: t('sensorType.options.flood'), value: BinarySensorTypeInternal.Flood },
          { label: t('sensorType.options.reedSwitch'), value: BinarySensorTypeInternal.ReedSwitch },
        ]}
        label={''}
      />
    </>
  );
};

export default BinarySensorType;
